/**
 * ServiceForm — поля формы доп. услуги (создание в .cform и правка в .medit,
 * эталон renderMkForm js:273-301 / renderMkList editing js:306-326):
 * Название/Короткое (frm2), Длительность — te-numbtn → NumPadPop (0 = «нет»),
 * OpsRow (A31), сворачиваемые «Расходники (N)» (ConsChips → нормы
 * service_consumables) и «Ресурсы (N)» (ResChips → service_resources); в
 * режиме правки — ЦЕНЫ 4 ячейки pn/pb/pp/mn (двойная точка входа с прайсом:
 * авто-сейв savePrice пишет mc_prices — одна истина, A38), ActionPair.
 * Хост владеет staging-стейтом (SvcFormState) — форма переживает re-render.
 */
import { useState, type Dispatch, type SetStateAction } from "react";
import type { MasterClass } from "../../api/types";
import { NumPadPop } from "../../components/shared";
import type { ServiceDraft, ServicesAdmin } from "../../hooks/useServicesAdmin";
import type { ConsItem } from "../../hooks/useQuestsAdmin";
import type { PriceField } from "../../hooks/usePricesAdmin";
import { ConsChips, ResChips } from "./ConsChips";
import { crewToOps, opsToCrew, OpsRow, type OpG } from "./OpsRow";
import { ActionPair, CollSec } from "./QuestFormCells";
import { cellPop, fmtNum, type PopCfg } from "./PriceRow";
import cfg from "./config.module.css";
import qcss from "./quests.module.css";
import css from "./services.module.css";

/** Optimistic-id — ОДИН на сессию формы создания: повторный «Создать» после
 *  частичного провала шлёт INSERT OR REPLACE по тому же id, а не дубль. */
const newSvcId = (): string =>
  `svc_${Date.now().toString(36)}${Math.floor(Math.random() * 1296).toString(36)}`;

/** Staging формы (эталон svcSt js:307: chips-секции авто-открыты при непустых). */
export interface SvcFormState {
  /** Optimistic-id создаваемой услуги (в правке не используется). */
  id: string;
  name: string;
  short: string;
  /** Минуты; 0 = «нет» (A39). */
  dur: number;
  ops: OpG[];
  cons: ConsItem[];
  res: string[];
  consOpen: boolean;
  resOpen: boolean;
}

export const svc0 = (): SvcFormState => ({
  id: newSvcId(),
  name: "", short: "", dur: 0, ops: [], cons: [], res: [], consOpen: false, resOpen: false,
});

export const svcFromService = (m: MasterClass, cons: ConsItem[], res: string[]): SvcFormState => ({
  id: newSvcId(),
  name: m.name,
  short: m.short_name ?? "",
  dur: m.duration ?? 0,
  ops: crewToOps(m.operators_comp),
  cons: cons.map((c) => ({ ...c })),
  res: [...res],
  consOpen: cons.length > 0,
  resOpen: res.length > 0,
});

export const svcDraft = (s: SvcFormState): ServiceDraft => ({
  id: s.id, name: s.name.trim(), short: s.short.trim(), dur: s.dur,
  crew: opsToCrew(s.ops), cons: s.cons, res: s.res,
});

/** Ряд цен формы — лейблы эталона priceRow (js:233), «Мин, ₽» оранжевый. */
const PRICE_LABS: [PriceField, string][] = [
  ["pn", "До, чел"], ["pb", "База, ₽"], ["pp", "+₽/чел"], ["mn", "Мин, ₽"],
];

export interface ServiceFormProps {
  st: SvcFormState;
  setSt: Dispatch<SetStateAction<SvcFormState>>;
  sa: ServicesAdmin;
  /** id услуги в режиме правки — включает ряд цен (mc_prices, авто-сейв). */
  editId?: string | null;
  busy?: boolean;
  /** Фокус на «Название» при маунте (эталон js:370-371: только открытие
   *  формы создания кнопкой «Добавить»; дубликат-свайп — без фокуса). */
  autoFocusName?: boolean;
  okLabel: string;
  onOk: () => void;
  onCancel: () => void;
}

export function ServiceForm({ st, setSt, sa, editId, busy, autoFocusName, okLabel, onOk, onCancel }: ServiceFormProps) {
  const [pop, setPop] = useState<PopCfg | null>(null);
  const up = (patch: Partial<SvcFormState>) => setSt((s) => ({ ...s, ...patch }));

  return (
    <>
      <div className={qcss.frm2}>
        <div className={qcss.gap2}>
          <span className={cfg.klbl}>Название{editId ? "" : " *"}</span>
          <input
            className={qcss.txt} maxLength={200} placeholder="Название услуги" value={st.name}
            autoFocus={!!autoFocusName}
            onChange={(e) => up({ name: e.target.value })}
          />
        </div>
        <div className={qcss.gap2}>
          <span className={cfg.klbl}>Короткое имя</span>
          {/* maxLength = лимит sanitize short_name на POST и PUT (50). */}
          <input
            className={qcss.txt} placeholder="Чип в списках" value={st.short}
            maxLength={50}
            onChange={(e) => up({ short: e.target.value })}
          />
        </div>
      </div>
      <div className={css.erow}>
        <span className={`${cfg.klbl} ${css.klblFlex}`}>Длительность</span>
        <button
          type="button" className={cfg.teNumbtn}
          onClick={(e) => setPop({
            anchor: e.currentTarget, label: "Длительность (0 — нет)", mode: "int",
            unit: "мин", max: 240, value: st.dur, onCommit: (v) => up({ dur: v }),
          })}
        >
          {st.dur ? <><span className={qcss.num}>{st.dur}</span>&nbsp;мин</> : "нет"}
        </button>
      </div>
      <OpsRow ops={st.ops} onChange={(ops) => up({ ops })} />
      <CollSec title="Расходники" count={st.cons.length} open={st.consOpen} onToggle={() => up({ consOpen: !st.consOpen })}>
        <ConsChips
          items={st.cons}
          cat={sa.consCat}
          onChange={(cons) => up({ cons })}
          /* Дефолт нового — /игрок (эталон js:266: per:'pl'). */
          onAdd={(id, qty) => setSt((s) => (s.cons.some((c) => c.consumable_id === id)
            ? s
            : { ...s, cons: [...s.cons, { consumable_id: id, qty, per_player: 1 }] }))}
          /* screen-18: qty-чип — NumPadPop у чипа (js:250-251), «+» — квадрат. */
          openQtyPop={(c) => setPop(c)}
          squareAdd
        />
      </CollSec>
      <CollSec title="Ресурсы" count={st.res.length} open={st.resOpen} onToggle={() => up({ resOpen: !st.resOpen })}>
        <ResChips ids={st.res} cat={sa.resCat} onChange={(res) => up({ res })} squareAdd />
      </CollSec>
      {editId ? (
        /* Двойная точка входа с прайсом: commit = savePrice → mc_prices. */
        <div className={css.priceGrid}>
          {PRICE_LABS.map(([f, lab]) => (
            <div key={f} className={cfg.cell}>
              <span className={`${cfg.klbl} ${f === "mn" ? css.mnLbl : ""}`}>{lab}</span>
              <button
                type="button" className={cfg.teNumbtn} style={{ alignSelf: "flex-start" }}
                onClick={(e) => setPop(cellPop(
                  st.name || "Услуга", f, sa.price(editId)[f], e.currentTarget,
                  (v) => sa.savePrice(editId, f, v),
                ))}
              >
                <span className={qcss.num}>{fmtNum(sa.price(editId)[f])}</span>
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {/* Гард пустого имени — только в создании (эталон js:290); правка
          сохраняет всегда, пустое имя падает на старое (js:322-323). */}
      <ActionPair okLabel={okLabel} onOk={onOk} onCancel={onCancel} okDisabled={!!busy || (!editId && !st.name.trim())} />
      <NumPadPop
        open={!!pop} anchor={pop?.anchor ?? null} label={pop?.label} mode={pop?.mode}
        unit={pop?.unit} max={pop?.max} value={pop?.value}
        onCommit={(v) => pop?.onCommit(v)} onClose={() => setPop(null)}
      />
    </>
  );
}
